import React from "react";
import { Mail, Instagram, ChevronRight, Briefcase, Sparkles, ArrowRight } from "lucide-react";
import { coaches, opportunities, siteConfig } from "../mock";
import { openExternal } from "../lib/external";

const EquipaPage = () => {
  return (
    <main>
      {/* Banner */}
      <section className="relative pt-32 pb-16 bg-brand-700 text-white overflow-hidden">
        <div
          className="absolute inset-0 opacity-50"
          style={{
            backgroundImage:
              "radial-gradient(circle at 90% 10%, rgba(245,130,32,0.25), transparent 50%), radial-gradient(circle at 10% 90%, rgba(229,50,109,0.18), transparent 50%)",
          }}
        />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 border border-white/20 backdrop-blur text-white text-xs font-medium tracking-wide mb-5">
            <Sparkles className="w-3.5 h-3.5 text-ember-400" />
            Época 2026/2027 arranca já em Setembro!
          </div>
          <h1 className="text-5xl md:text-6xl font-extrabold tracking-tight leading-tight">
            Conhece a{" "}
            <span className="bg-gradient-to-r from-ember-400 to-ember-600 bg-clip-text text-transparent">
              equipa técnica
            </span>
          </h1>
          <p className="mt-5 text-lg text-white/75 max-w-2xl">
            Conhece os profissionais dedicados que fazem do CATV um clube de
            excelência no atletismo português.
          </p>
        </div>
      </section>

      {/* Coaches grid */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {coaches.map((c) => (
              <article
                key={c.id}
                className="group relative bg-white rounded-3xl overflow-hidden border border-stone-100 shadow-sm hover:shadow-2xl transition-all duration-300 flex flex-col"
              >
                <div className="relative aspect-[4/5] overflow-hidden bg-stone-100">
                  <img
                    src={c.image}
                    alt={`Fotografia de ${c.name}`}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    onError={(e) => {
                      e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(
                        c.name
                      )}&background=2D2A8F&color=fff&size=400`;
                    }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-brand-900/90 via-brand-900/30 to-transparent" />
                  <div className="absolute bottom-5 left-5 right-5">
                    <h3 className="text-2xl font-bold text-white">{c.name}</h3>
                    <p className="text-ember-300 text-sm mt-1">{c.role}</p>
                  </div>
                </div>
                <div className="p-6 flex-1 flex flex-col">
                  <ul className="space-y-2 mb-5 flex-1">
                    {c.bio.map((line, i) => (
                      <li
                        key={i}
                        className="flex items-start gap-2 text-sm text-stone-600"
                      >
                        <ChevronRight className="w-4 h-4 text-ember-500 mt-0.5 shrink-0" />
                        <span>{line}</span>
                      </li>
                    ))}
                  </ul>
                  <div className="flex items-center gap-3 pt-4 border-t border-stone-100">
                    <a
                      href={`mailto:${siteConfig.email}`}
                      aria-label="Email"
                      className="w-9 h-9 rounded-full bg-stone-100 hover:bg-brand-700 hover:text-white text-stone-600 flex items-center justify-center transition-colors"
                    >
                      <Mail className="w-4 h-4" />
                    </a>
                    <a
                      href={siteConfig.instagram}
                      onClick={openExternal(siteConfig.instagram)}
                      aria-label="Instagram"
                      className="w-9 h-9 rounded-full bg-stone-100 hover:bg-gradient-to-br hover:from-ember-400 hover:to-ember-600 hover:text-white text-stone-600 flex items-center justify-center transition-colors cursor-pointer"
                    >
                      <Instagram className="w-4 h-4" />
                    </a>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Opportunities */}
      <section className="py-20 bg-stone-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="max-w-2xl mb-12">
            <p className="font-semibold text-sm uppercase tracking-widest mb-3 bg-gradient-to-r from-ember-500 to-ember-600 bg-clip-text text-transparent">
              Carreiras
            </p>
            <h2 className="text-4xl md:text-5xl font-bold text-stone-900 tracking-tight">
              Junta-te à nossa equipa
            </h2>
            <p className="mt-4 text-stone-600 text-lg">
              Interessado em integrar a nossa equipa técnica ou de apoio? Consulta
              as nossas oportunidades ou entra em contacto connosco.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {opportunities.map((op, idx) => (
              <a
                key={op.id}
                href={`mailto:${siteConfig.email}?subject=${encodeURIComponent(
                  op.title
                )}`}
                className="group relative overflow-hidden rounded-3xl bg-white border border-stone-100 hover:shadow-2xl transition-shadow"
              >
                <div className="aspect-[16/9] overflow-hidden bg-brand-50">
                  <img
                    src={op.image}
                    alt={op.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    onError={(e) => {
                      e.target.src = `https://images.unsplash.com/photo-${
                        idx === 0
                          ? "1521737711867-e3b97375f902"
                          : "1571019613454-1cb2f99b2d8b"
                      }?w=900`;
                    }}
                  />
                </div>
                <div className="p-7">
                  <div className="flex items-center gap-2 text-ember-600 text-xs uppercase tracking-widest font-semibold mb-3">
                    <Briefcase className="w-3.5 h-3.5" />
                    {idx === 0 ? "Emprego" : "Voluntário"}
                  </div>
                  <h3 className="text-2xl font-bold text-stone-900">{op.title}</h3>
                  <p className="mt-3 text-stone-600">{op.description}</p>
                  <div className="mt-5 inline-flex items-center gap-2 text-brand-700 font-semibold group-hover:gap-3 transition-all">
                    {op.cta} <ArrowRight className="w-4 h-4" />
                  </div>
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
};

export default EquipaPage;
